import { useState, useEffect } from 'react';
import { listParamFormulas, deleteParamFormula, upsertParamFormula } from '../services/paramFormulaService';
import type { ParamFormula } from '../services/paramFormulaService';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

export default function ParamFormulaPage() {
  const [formulas, setFormulas] = useState<ParamFormula[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Add form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [addName, setAddName] = useState('');
  const [addFormula, setAddFormula] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listParamFormulas();
      setFormulas(data);
    } catch {
      setError('Failed to load param formulas.');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleAdd() {
    if (!addName.trim() || !addFormula.trim()) {
      setAddError('Both name and formula are required.');
      return;
    }
    setIsSaving(true);
    setAddError(null);
    try {
      const saved = await upsertParamFormula({
        paramName: addName.trim(),
        naturalLanguageFormula: addFormula.trim(),
      });
      setFormulas((prev) => {
        const existing = prev.findIndex((f) => f.paramName === saved.paramName);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = saved;
          return updated.sort((a, b) => a.paramName.localeCompare(b.paramName));
        }
        return [...prev, saved].sort((a, b) => a.paramName.localeCompare(b.paramName));
      });
      setAddName('');
      setAddFormula('');
      setShowAddForm(false);
    } catch {
      setAddError('Failed to save formula.');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(formulaId: number, paramName: string) {
    if (!confirm(`Delete formula for "${paramName}"?`)) return;
    setDeletingId(formulaId);
    try {
      await deleteParamFormula(formulaId);
      setFormulas((prev) => prev.filter((f) => f.formulaId !== formulaId));
    } catch {
      setError('Failed to delete formula.');
    } finally {
      setDeletingId(null);
    }
  }

  const filtered = formulas.filter((f) =>
    f.paramName.toLowerCase().includes(search.toLowerCase()) ||
    f.naturalLanguageFormula.toLowerCase().includes(search.toLowerCase())
  );

  if (isLoading) return <LoadingSpinner message="Loading param formulas..." />;

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
        <h2 style={{ margin: 0 }}>Param Formula Library</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
            {formulas.length} saved formula{formulas.length !== 1 ? 's' : ''}
          </span>
          <Button type="button" size="sm" onClick={() => { setShowAddForm(!showAddForm); setAddError(null); }}>
            {showAddForm ? 'Cancel' : '+ Add Formula'}
          </Button>
        </div>
      </div>

      <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1rem' }}>
        Formulas are auto-saved when you add calculated parameters to agreements.
        Type <strong>@</strong> in any formula field while creating or editing an agreement to reuse them.
      </p>

      {/* Add form */}
      {showAddForm && (
        <div style={{
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          padding: '1rem',
          backgroundColor: '#f9fafb',
          marginBottom: '1.25rem',
        }}>
          <div style={{ fontWeight: 600, marginBottom: '0.75rem', fontSize: '0.9rem' }}>Add / Update Formula</div>
          {addError && <Alert type="error" onDismiss={() => setAddError(null)}>{addError}</Alert>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <input
              type="text"
              placeholder="Parameter name (e.g. ProtectionKitCharges_Agreement)"
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem', border: '1px solid #d1d5db', borderRadius: '6px', outline: 'none' }}
            />
            <textarea
              placeholder="Natural language formula (e.g. if ProtectionKitCharge > 0 then minimum of ProtectionKitCharge, 10000 else 0)"
              value={addFormula}
              onChange={(e) => setAddFormula(e.target.value)}
              rows={3}
              style={{ padding: '0.5rem 0.75rem', fontSize: '0.9rem', border: '1px solid #d1d5db', borderRadius: '6px', outline: 'none', resize: 'vertical' }}
            />
            <div>
              <Button type="button" size="sm" onClick={handleAdd} isLoading={isSaving} disabled={isSaving}>
                Save Formula
              </Button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <Alert type="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div style={{ marginBottom: '1rem' }}>
        <input
          type="text"
          placeholder="Search by name or formula..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: '0.5rem 0.75rem',
            fontSize: '0.9rem',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            width: '320px',
            outline: 'none',
          }}
          aria-label="Search param formulas"
        />
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
          {formulas.length === 0
            ? 'No formulas saved yet. They will appear here after you add calculated parameters to agreements.'
            : 'No formulas match your search.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filtered.map((f) => (
            <div
              key={f.formulaId}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '1rem',
                backgroundColor: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#111827', marginBottom: '0.35rem' }}>
                    {f.paramName}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#374151', marginBottom: '0.25rem' }}>
                    <span style={{ color: '#6b7280', marginRight: '0.4rem' }}>Formula:</span>
                    {f.naturalLanguageFormula}
                  </div>
                  {f.executableFormula && (
                    <div style={{ fontSize: '0.78rem', color: '#6b7280', fontFamily: 'monospace', marginTop: '0.25rem', wordBreak: 'break-all' }}>
                      {f.executableFormula}
                    </div>
                  )}
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.4rem' }}>
                    Last updated: {new Date(f.modifiedAt).toLocaleDateString()}
                    {f.modifiedBy ? ` by ${f.modifiedBy}` : ''}
                  </div>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => handleDelete(f.formulaId, f.paramName)}
                  disabled={deletingId === f.formulaId}
                  isLoading={deletingId === f.formulaId}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
